import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import compose from 'lodash.flowright';

import usePurchasesPendingConfiguration from './hooks/usePurchasesPendingConfiguration';
import Button from '../../../../../../../components/global/Button';
import Angle from '../../../../../../../components/global/Angle';
import {
  cartItemUpdate,
  togglePurchasedPrintPackageCartItem,
} from '../../../../../../../services/checkout/redux/checkoutActions';
import { getCheckoutCart } from '../../../../../../../services/checkout/redux/checkoutSelectors';
import AlbumPrintPackageOptionList from './components/AlbumPrintPackageOptionList';
import { PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE_MODAL } from './components/PurchasedPrintPackageConfigurationCompleteModal';
import useClickOutside from '../../../../../../../hooks/useClickOutside';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import PhotoPricingOptionList from './components/PhotoPricingOptionList';
import CustomPrintOptionList from './components/CustomPrintOptionList';

const propTypes = {
  hierarchyItemId: PropTypes.string.isRequired,
  onCheckout: PropTypes.func.isRequired,
  onExpand: PropTypes.func,
  startExpanded: PropTypes.bool,
  className: PropTypes.string,
  shippableOnly: PropTypes.bool,
  optionProps: PropTypes.object,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PurchasesPendingConfiguration = ({
  hierarchyItemId,
  onCheckout,
  onExpand,
  startExpanded,
  className,
  shippableOnly,
  optionProps,
  watermarkSettings,
}) => {
  const ref = useRef();
  const openModal = useOpenModal();
  const [expanded, setExpanded] = useState(startExpanded ?? false);
  // Only rely on this if we've not been provided explicit onExpand commands.
  useClickOutside(ref, () => {
    if (!onExpand) {
      setExpanded(false);
    }
  });

  const {
    albumMembershipGroup,
    prepaidPrintPackages,
    prepaidPhotoPricings,
    prepaidCustomPrints,
    albumPrintPackages,
    photoPricing,
    isAllPhotos,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId,
    shippableOnly,
  });

  // If we have neither, relevant print packages, or relevant photo pricing
  // we will return null.
  if (
    (!prepaidPrintPackages ||
      prepaidPrintPackages.length === 0 ||
      !albumPrintPackages ||
      albumPrintPackages.length === 0) &&
    (!prepaidPhotoPricings ||
      prepaidPhotoPricings.length === 0 ||
      !photoPricing) &&
    (!prepaidCustomPrints || prepaidCustomPrints.length === 0)
  ) {
    return null;
  }

  const classes = classNames({
    'purchases-pending-configuration-collapsable': true,
    'is-expanded': expanded,
    [className]: !!className,
  });

  const onOptionConfigurationComplete = () => {
    openModal(PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE_MODAL, {
      watermarkSettings,
    });
  };

  return (
    <div className={classes} ref={ref}>
      <Button
        className="purchases-pending-configuration-header"
        onClick={() => {
          if (onExpand) {
            onExpand({ expanded, setExpanded });
          } else {
            setExpanded(prev => !prev);
          }
        }}
      >
        <span>
          {pluralize(
            'purchase',
            prepaidPrintPackages.length +
              prepaidPhotoPricings.length +
              prepaidCustomPrints.length,
            true,
          )}{' '}
          pending configuration
        </span>
        <Angle direction={expanded ? 'down' : 'up'} />
      </Button>
      <div className="purchases-pending-configuration-content">
        <AlbumPrintPackageOptionList
          isAllPhotos={isAllPhotos}
          albumMembershipGroup={albumMembershipGroup}
          prepaidPrintPackages={prepaidPrintPackages}
          albumPrintPackages={albumPrintPackages}
          hasPurchasedPhotoPricings={prepaidPhotoPricings?.length > 0}
          onCheckout={onCheckout}
          optionProps={{
            onConfigurationComplete: onOptionConfigurationComplete,
            watermarkSettings,
            ...(optionProps ?? {}),
          }}
        />
        <PhotoPricingOptionList
          isAllPhotos={isAllPhotos}
          albumMembershipGroup={albumMembershipGroup}
          prepaidPhotoPricings={prepaidPhotoPricings}
          onCheckout={onCheckout}
          optionProps={{
            onConfigurationComplete: onOptionConfigurationComplete,
            watermarkSettings,
            ...(optionProps ?? {}),
          }}
        />
        <CustomPrintOptionList
          isAllPhotos={isAllPhotos}
          albumMembershipGroup={albumMembershipGroup}
          prepaidCustomPrints={prepaidCustomPrints}
          onCheckout={onCheckout}
          optionProps={{
            onConfigurationComplete: onOptionConfigurationComplete,
            watermarkSettings,
            ...(optionProps ?? {}),
          }}
        />
      </div>
    </div>
  );
};

PurchasesPendingConfiguration.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
});

const mapDispatchToProps = {
  onCartItemUpdate: cartItemUpdate,
  onPurchasedPrintPackageToggle: togglePurchasedPrintPackageCartItem,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(PurchasesPendingConfiguration);
